import React, {
  createContext,
  useContext,
  useMemo,
  type CSSProperties,
  type PropsWithChildren,
} from 'react';
import { useSortable } from '@dnd-kit/sortable';
import type { UniqueIdentifier } from '@dnd-kit/core';
import type { DraggableSyntheticListeners, DraggableAttributes } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicatorRounded } from '@mui/icons-material';

interface SortableItemContextValue {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners | undefined;
  ref: (node: HTMLElement | null) => void;
}

const SortableItemContext = createContext<SortableItemContextValue>({
  attributes: {} as DraggableAttributes,
  listeners: undefined,
  ref: () => {},
});

export function DraggableListItem({ children, id }: PropsWithChildren<{ id: UniqueIdentifier }>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const contextValue = useMemo(
    () => ({ attributes, listeners, ref: setActivatorNodeRef }),
    [attributes, listeners, setActivatorNodeRef]
  );

  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <SortableItemContext.Provider value={contextValue}>
      <li ref={setNodeRef} style={style}>
        {children}
      </li>
    </SortableItemContext.Provider>
  );
}

export function DragHandle({ className }: { className?: string }) {
  const { attributes, listeners, ref } = useContext(SortableItemContext);

  return (
    <button
      {...attributes}
      {...listeners}
      ref={ref}
      className={className}
      style={{ cursor: 'grab', background: 'transparent', border: 'none', padding: 0 }}
    >
      <DragIndicatorRounded sx={{ color: 'primary.400' }} />
    </button>
  );
}
